<template>
  <section class="analysis-detail-page">
    <div class="analysis-shell">
      <div class="topbar">
        <button class="nav-chip" @click="goBack">{{ t("backToAnalyzer") }}</button>
        <button class="nav-chip subtle" :disabled="loading" @click="loadJob">
          {{ loading ? t("loadingResult") : t("refreshResult") }}
        </button>
        <button class="nav-chip danger-chip" :disabled="deleting" @click="deleteJob">
          {{ deleting ? t("deletingJob") : t("deleteJob") }}
        </button>
      </div>

      <p v-if="loading" class="state-card">{{ t("loadingDetail") }}</p>
      <p v-else-if="errorMessage" class="error-panel">{{ errorMessage }}</p>

      <template v-else-if="job">
        <header class="hero-card panel-card">
          <div>
            <p class="eyebrow">{{ t("reportEyebrow") }}</p>
            <h1>{{ job.originalFileName }}</h1>
            <p class="lead">{{ t("jobId") }}: {{ job.jobId }}</p>
            <div class="chip-row">
              <span class="soft-badge">{{ formatPracticeType(job.practiceType) }}</span>
              <span class="status-chip">{{ formatStatus(job.status) }}</span>
              <span class="soft-badge">{{ t("viewAngle") }} {{ formatViewAngle(job.result?.viewAngle) }}</span>
            </div>
          </div>

          <div class="meta-grid">
            <div class="meta-box">
              <span>{{ t("duration") }}</span>
              <strong>{{ job.result?.durationSeconds || 0 }} s</strong>
            </div>
            <div class="meta-box">
              <span>{{ t("frames") }}</span>
              <strong>{{ job.result?.frameCount || 0 }}</strong>
            </div>
            <div class="meta-box">
              <span>{{ t("fps") }}</span>
              <strong>{{ job.result?.fps || 0 }}</strong>
            </div>
            <div class="meta-box">
              <span>{{ t("createdAt") }}</span>
              <strong>{{ formatDateTime(job.createdAt) }}</strong>
            </div>
          </div>
        </header>

        <section class="panel-card">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("videoTag") }}</p>
              <h2>{{ t("sourceVideo") }}</h2>
            </div>
          </div>

          <video v-if="job.inputVideoUrl" class="player" controls :src="toAbsoluteUrl(job.inputVideoUrl)"></video>
          <p v-else class="empty-state">{{ t("noVideo") }}</p>
        </section>

        <section class="panel-card" v-if="job.result?.artifacts?.length">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("artifactsTag") }}</p>
              <h2>{{ t("artifacts") }}</h2>
            </div>
          </div>

          <div class="artifact-list">
            <a
              v-for="artifact in job.result.artifacts"
              :key="artifact.artifactKey"
              class="artifact-link"
              :href="toAbsoluteUrl(artifact.url)"
              target="_blank"
              rel="noreferrer"
            >
              {{ formatArtifactLabelText(artifact) }}
            </a>
          </div>
        </section>

        <section class="panel-card" v-if="job.result?.keyframes?.length">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("keyframesTag") }}</p>
              <h2>{{ t("keyframes") }}</h2>
            </div>
          </div>

          <div class="keyframe-grid">
            <article v-for="frame in job.result.keyframes" :key="frame.keyframeKey" class="keyframe-card">
              <img class="keyframe-image" :src="toAbsoluteUrl(frame.imageUrl)" :alt="formatPhase(frame)" />
              <div class="keyframe-body">
                <div class="phase-topline">
                  <strong>{{ formatPhase(frame) }}</strong>
                  <span>{{ t("frameNo") }} {{ frame.frameIndex }}</span>
                </div>
                <p class="keyframe-meta">{{ t("atTime") }}: {{ frame.timestampSeconds.toFixed(2) }}s</p>
                <div class="observation-list">
                  <div v-for="item in frame.observations || []" :key="`${frame.keyframeKey}-${item.metricKey}`" class="observation-item">
                    <strong>{{ formatMetric(item) }}</strong>
                    <span>{{ formatMetricValue(item) }}</span>
                    <p>{{ formatMetricSummaryText(item) }}</p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section class="panel-card" v-if="hasAiAnalysis">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ localize(AI_TEXT.tag, "AI ANALYSIS") }}</p>
              <h2>{{ localize(AI_TEXT.title, "AI Pose Review") }}</h2>
            </div>
            <div class="chip-row">
              <span :class="['severity-chip', aiStatusSeverity(job.result?.aiAnalysis?.status)]">
                {{ formatAiStatus(job.result?.aiAnalysis?.status) }}
              </span>
              <span v-if="job.result?.aiAnalysis?.model" class="soft-badge">
                {{ localize(AI_TEXT.model, "Model") }} {{ job.result.aiAnalysis.model }}
              </span>
            </div>
          </div>

          <p class="phase-note">{{ job.result?.aiAnalysis?.message || localize(AI_TEXT.noMessage, "No AI message.") }}</p>

          <div v-if="hasAiVersions" class="ai-grid">
            <article
              v-for="version in job.result?.aiAnalysis?.versions || []"
              :key="version.techniqueVersionId || version.versionNo"
              class="ai-card"
            >
              <div class="phase-topline">
                <strong>{{ localize(AI_TEXT.version, "Version") }} {{ version.versionNo }}</strong>
                <div class="chip-row">
                  <span v-if="version.isCurrent" class="soft-badge">{{ localize(AI_TEXT.current, "Current") }}</span>
                  <span :class="['severity-chip', version.motion?.matches ? 'info' : 'warning']">
                    {{ formatAiMatch(version.motion?.matches) }}
                  </span>
                </div>
              </div>

              <div class="list-block">
                <strong>{{ localize(AI_TEXT.motion, "Motion") }}</strong>
                <p>{{ version.motion?.reason || localize(AI_TEXT.noMotionReason, "No motion reasoning returned.") }}</p>
              </div>

              <div class="list-block">
                <strong>{{ localize(AI_TEXT.missingPoints, "Missing Points") }}</strong>
                <ul v-if="version.missingPoints?.length" class="plain-list">
                  <li v-for="item in version.missingPoints" :key="`${version.versionNo}-${item}`">{{ item }}</li>
                </ul>
                <p v-else>{{ localize(AI_TEXT.none, "None") }}</p>
              </div>

              <div class="list-block">
                <strong>{{ localize(AI_TEXT.advice, "Advice") }}</strong>
                <ul v-if="version.advice?.length" class="plain-list">
                  <li v-for="item in version.advice" :key="`${version.versionNo}-${item}`">{{ item }}</li>
                </ul>
                <p v-else>{{ localize(AI_TEXT.none, "None") }}</p>
              </div>
            </article>
          </div>
        </section>

        <section
          v-if="hasPhases || hasFeedback"
          :class="['split-stage', { single: !(hasPhases && hasFeedback) }]"
        >
          <article v-if="hasPhases" class="panel-card">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("phasesTag") }}</p>
                <h2>{{ t("phases") }}</h2>
              </div>
            </div>

            <div class="phase-list">
              <article v-for="phase in job.result?.phases || []" :key="phase.phaseKey" class="phase-card">
                <div class="phase-topline">
                  <strong>{{ formatPhase(phase) }}</strong>
                  <span>{{ phase.startSeconds.toFixed(2) }}s - {{ phase.endSeconds.toFixed(2) }}s</span>
                </div>
                <div class="phase-bar">
                  <span :style="{ width: `${phaseWidth(phase)}%` }"></span>
                </div>
                <p class="phase-note">{{ formatPhaseWeightText(phase) }}</p>
                <p class="phase-note subtle">{{ formatPhaseMeaningText(phase) }}</p>
              </article>
            </div>
          </article>

          <article v-if="hasFeedback" class="panel-card">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("feedbackTag") }}</p>
                <h2>{{ t("feedback") }}</h2>
              </div>
            </div>

            <div class="feedback-list">
              <article v-for="item in job.result?.feedback || []" :key="item.feedbackKey" class="feedback-card">
                <div class="phase-topline">
                  <strong>{{ formatFeedbackTitleText(item) }}</strong>
                  <span :class="['severity-chip', item.severity]">{{ formatSeverity(item.severity) }}</span>
                </div>
                <p>{{ formatFeedbackMessageText(item) }}</p>
              </article>
            </div>
          </article>
        </section>

        <section class="panel-card">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("metricsTag") }}</p>
              <h2>{{ t("metrics") }}</h2>
            </div>
          </div>

          <div class="metric-grid">
            <article v-for="metric in job.result?.metrics || []" :key="metric.metricKey" class="metric-card">
              <div class="phase-topline">
                <strong>{{ formatMetric(metric) }}</strong>
                <span :class="['severity-chip', metric.level]">{{ formatSeverity(metric.level) }}</span>
              </div>
              <p class="metric-value">{{ formatMetricValue(metric) }}</p>
              <p class="metric-target">{{ t("target") }}: {{ formatMetricTarget(metric) }}</p>
              <p v-if="formatMetricTargetReason(metric)" class="metric-target-reason">{{ formatMetricTargetReason(metric) }}</p>
              <p>{{ formatMetricSummaryText(metric) }}</p>
            </article>
          </div>
        </section>
      </template>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  formatArtifactLabel,
  formatFeedbackMessage,
  formatFeedbackTitle,
  formatMetricLabel,
  formatMetricSummary,
  formatPhaseLabel,
  formatPracticeTypeLabel,
  formatSeverityLabel,
  formatStatusLabel,
  formatViewAngleLabel,
  localeForLanguage,
  tAnalyzer,
} from "../../shared/analyzerI18n";
import { useSiteLanguage } from "../../store/siteLanguage";
import { API_BASE, ensureApiObject } from "./portalShared";

const route = useRoute();
const router = useRouter();
const { lang } = useSiteLanguage();
const currentLocale = computed(() => localeForLanguage(lang.value));
const job = ref(null);
const loading = ref(true);
const errorMessage = ref("");
const deleting = ref(false);
const hasPhases = computed(() => Boolean(job.value?.result?.phases?.length));
const hasFeedback = computed(() => Boolean(job.value?.result?.feedback?.length));
const hasAiAnalysis = computed(() => Boolean(job.value?.result?.aiAnalysis));
const hasAiVersions = computed(() => Boolean(job.value?.result?.aiAnalysis?.versions?.length));

const LOCALIZED_TEXT = {
  phaseMeaning: {
    ja: "このバーは、その段階が動画全体のどれくらいの時間を占めるかを表します。",
    zh: "这条进度条表示该阶段在整段视频时长中的占比。",
    en: "This bar shows how much of the full video duration is covered by this phase.",
  },
  phaseWeight: {
    ja: "{phase}が動画時間に占める割合 {value}",
    zh: "{phase}占视频时长 {value}",
    en: "{phase} covers {value} of the video",
  },
  targetBasis: {
    ja: "基準",
    zh: "依据",
    en: "Basis",
  },
  unitDeg: {
    ja: "度",
    zh: "度",
    en: "deg",
  },
  unitRatio: {
    ja: "倍肩幅",
    zh: "倍肩宽",
    en: "x shoulder width",
  },
  unitNorm: {
    ja: "正規化値",
    zh: "归一化值",
    en: "normalized",
  },
  atLeast: {
    ja: "{value}以上",
    zh: "至少 {value}",
    en: "At least {value}",
  },
  atMost: {
    ja: "{value}以下",
    zh: "不超过 {value}",
    en: "No more than {value}",
  },
  around: {
    ja: "{value}前後",
    zh: "接近 {value}",
    en: "Around {value}",
  },
  uprightReference: {
    ja: "直立から軽い前傾まで。まずは {value} を基準にします。",
    zh: "直立到微前倾，先以 {value} 作为参考中心。",
    en: "Stay upright or only slightly forward, using {value} as the first reference.",
  },
};

const AI_TEXT = {
  tag: { ja: "AI ANALYSIS", zh: "AI ANALYSIS", en: "AI ANALYSIS" },
  title: { ja: "AI 姿勢レビュー", zh: "AI 动作复核", en: "AI Pose Review" },
  model: { ja: "モデル", zh: "模型", en: "Model" },
  version: { ja: "版", zh: "版本", en: "Version" },
  current: { ja: "現行", zh: "当前", en: "Current" },
  motion: { ja: "動作", zh: "动作", en: "Motion" },
  missingPoints: { ja: "足りないところ", zh: "不到位的地方", en: "Missing Points" },
  advice: { ja: "アドバイス", zh: "建议", en: "Advice" },
  none: { ja: "なし", zh: "无", en: "None" },
  noMessage: { ja: "AI からの補足はありません。", zh: "AI 没有返回补充说明。", en: "No AI note returned." },
  noMotionReason: { ja: "AI が動作理由を返しませんでした。", zh: "AI 没有返回动作判断理由。", en: "The AI did not return a motion reason." },
  statusCompleted: { ja: "完了", zh: "完成", en: "Completed" },
  statusSkipped: { ja: "未実行", zh: "未执行", en: "Skipped" },
  statusFailed: { ja: "失敗", zh: "失败", en: "Failed" },
  matchYes: { ja: "一致", zh: "匹配", en: "Matches" },
  matchNo: { ja: "不一致", zh: "不匹配", en: "Does Not Match" },
};

function t(key) {
  return tAnalyzer(key, lang.value);
}

function localize(entry, fallback = "") {
  return entry?.[lang.value] || entry?.ja || entry?.zh || entry?.en || fallback;
}

function interpolate(template, values) {
  return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template || "");
}

function formatPracticeType(value) {
  return formatPracticeTypeLabel(value, lang.value);
}

function formatStatus(value) {
  return formatStatusLabel(value, lang.value);
}

function formatViewAngle(value) {
  return formatViewAngleLabel(value, lang.value);
}

function formatPhase(phase) {
  return formatPhaseLabel(phase.phaseKey, phase.label, lang.value);
}

function formatMetric(metric) {
  return formatMetricLabel(metric.metricKey, metric.label, lang.value);
}

function formatMetricSummaryText(metric) {
  return formatMetricSummary(metric.metricKey, metric.summary, lang.value);
}

function formatMetricScalar(metricKey, value, unit) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (unit === "%") {
    return `${value}%`;
  }

  if (unit === "deg") {
    return `${value} ${localize(LOCALIZED_TEXT.unitDeg, "deg")}`;
  }

  if (unit === "ratio") {
    return `${value} ${localize(LOCALIZED_TEXT.unitRatio, "x shoulder width")}`;
  }

  if (unit === "norm") {
    return `${value} ${localize(LOCALIZED_TEXT.unitNorm, "normalized")}`;
  }

  return unit ? `${value} ${unit}` : `${value}`;
}

function formatMetricValue(metric) {
  return formatMetricScalar(metric.metricKey, metric.value, metric.unit);
}

function formatMetricTarget(metric) {
  const targetValue = formatMetricScalar(metric.metricKey, metric.target, metric.unit);

  switch (metric.metricKey) {
    case "chudanSimilarity":
    case "poseDetectionRate":
    case "leftHeelLiftRatio":
      return interpolate(localize(LOCALIZED_TEXT.atLeast), { value: targetValue });
    case "stanceWidthRatio":
      return interpolate(localize(LOCALIZED_TEXT.around), { value: targetValue });
    case "upperBodyLean":
      return interpolate(localize(LOCALIZED_TEXT.uprightReference), { value: targetValue });
    case "footForwardAngleDeg":
    case "footAlignmentOffset":
    case "elbowBodyDistance":
    case "wristCenterDeviation":
    case "headFacingPlaneDeg":
    case "shoulderTiltDeg":
    case "leftWristCenterOffset":
      return interpolate(localize(LOCALIZED_TEXT.atMost), { value: targetValue });
    default:
      return targetValue;
  }
}

function formatMetricTargetReason(metric) {
  const basisLabel = localize(LOCALIZED_TEXT.targetBasis, "Basis");
  const targetValue = formatMetricScalar(metric.metricKey, metric.target, metric.unit);
  let message = "";

  switch (metric.metricKey) {
    case "chudanSimilarity":
      message = localize({
        ja: `${targetValue} は現在の受け入れ下限です。これを下回ると、中段構えの条件が十分そろっていない動画として扱います。`,
        zh: `${targetValue} 是当前版本的识别下限。低于这个值时，会先判定为不够像中段构的视频。`,
        en: `${targetValue} is the current acceptance floor. Below it, the clip is treated as not matching chudan-no-kamae strongly enough.`,
      });
      break;
    case "footForwardAngleDeg":
      message = localize({
        ja: `${targetValue} は、つま先が前方 X 軸から大きく外れていないと見なす初版しきい値です。`,
        zh: `${targetValue} 是第一版阈值，表示双脚脚尖基本没有明显偏离前方 X 轴。`,
        en: `${targetValue} is the first-pass threshold for treating both feet as basically facing the forward X axis.`,
      });
      break;
    case "footAlignmentOffset":
      message = localize({
        ja: `${targetValue} は、左足先と右かかとの前後ずれを肩幅の 6% 以内に収め、ほぼ同一直線とみなすための初版基準です。`,
        zh: `${targetValue} 是第一版阈值，表示左脚尖与右脚跟的前后错位控制在肩宽 6% 以内，可视为基本在一条线上。`,
        en: `${targetValue} is the first-pass threshold for keeping the left-toe and right-heel offset within about 6% of shoulder width.`,
      });
      break;
    case "stanceWidthRatio":
      message = localize({
        ja: `${targetValue} は、まず肩幅前後を中段構えの基準幅として置いた初版設定です。`,
        zh: `${targetValue} 表示先把肩宽附近作为中段构的标准站位宽度，再围绕它继续调样本。`,
        en: `${targetValue} uses shoulder-width stance as the first reference point for chudan-no-kamae.`,
      });
      break;
    case "leftHeelLiftRatio":
      message = localize({
        ja: `${targetValue} は、左かかとを高く上げすぎず、前足部に軽く荷重が乗っている状態を拾うための最小基準です。`,
        zh: `${targetValue} 是左脚跟轻轻离地、但不过度抬高的第一版最小值，用来体现前脚掌着力。`,
        en: `${targetValue} is the minimum first-pass lift used to capture a lightly raised left heel with load on the ball of the foot.`,
      });
      break;
    case "elbowBodyDistance":
      message = localize({
        ja: `${targetValue} は、「肘を腰に収める」を肩幅比に直した初版の容許値です。`,
        zh: `${targetValue} 是把“肘贴腰”换算成肩宽比例后的第一版容许值。`,
        en: `${targetValue} is the first-pass allowance for keeping the elbows close to the torso lines in shoulder-width terms.`,
      });
      break;
    case "wristCenterDeviation":
      message = localize({
        ja: `${targetValue} は、左右の手首を中心面近くに保つために置いた肩幅 10% 相当の基準です。`,
        zh: `${targetValue} 来自前面确认过的中心面容许值，表示双腕偏离 XOZ 面尽量控制在肩宽 10% 以内。`,
        en: `${targetValue} is the current tolerance for keeping both wrists near the XOZ center plane, set at about 10% of shoulder width.`,
      });
      break;
    case "upperBodyLean":
      message = localize({
        ja: `${targetValue} は、直立から軽い前傾までを許容するために置いた暫定の中心値です。`,
        zh: `${targetValue} 是“直立到微前倾”这条要求的暂定参考中心，用来先排除明显前倒或后仰。`,
        en: `${targetValue} is the provisional reference center for keeping the upper body upright or only slightly forward.`,
      });
      break;
    case "headFacingPlaneDeg":
      message = localize({
        ja: `${targetValue} は、頭頂と両耳の面が相手へほぼ正対しているとみなす初版しきい値です。`,
        zh: `${targetValue} 是第一版阈值，用来判断头顶与双耳构成的面是否基本正对对手。`,
        en: `${targetValue} is the first-pass threshold for treating the head-top and ear plane as roughly square to the opponent.`,
      });
      break;
    case "poseDetectionRate":
      message = localize({
        ja: `${targetValue} は、分析結果を安定して読める最低限に近いカバー率として置いています。`,
        zh: `${targetValue} 表示姿态覆盖率至少要到这个水平，分析结果才比较稳定。`,
        en: `${targetValue} is used as a practical floor for pose coverage so the rest of the analysis stays readable.`,
      });
      break;
    case "shoulderTiltDeg":
      message = localize({
        ja: `${targetValue} は、肩線がほぼ水平と見なせる初版の上限です。`,
        zh: `${targetValue} 是把双肩视为基本水平的第一版上限。`,
        en: `${targetValue} is the first-pass ceiling for treating the shoulder line as basically level.`,
      });
      break;
    case "leftWristCenterOffset":
      message = localize({
        ja: `${targetValue} は、左手が体の中心線から大きく外れていないと見るための初版基準です。`,
        zh: `${targetValue} 是第一版阈值，用来判断左手是否明显偏离身体中心线。`,
        en: `${targetValue} is the first-pass threshold for keeping the left wrist reasonably close to the body center line.`,
      });
      break;
    default:
      return "";
  }

  return `${basisLabel}: ${message}`;
}

function formatFeedbackTitleText(item) {
  return formatFeedbackTitle(item.feedbackKey, item.title, lang.value);
}

function formatFeedbackMessageText(item) {
  return formatFeedbackMessage(item.feedbackKey, item.message, lang.value);
}

function formatArtifactLabelText(artifact) {
  return formatArtifactLabel(artifact.artifactKey, artifact.label, lang.value);
}

function formatSeverity(value) {
  return formatSeverityLabel(value, lang.value);
}

function formatAiStatus(value) {
  switch (value) {
    case "completed":
      return localize(AI_TEXT.statusCompleted, "Completed");
    case "failed":
      return localize(AI_TEXT.statusFailed, "Failed");
    case "skipped":
      return localize(AI_TEXT.statusSkipped, "Skipped");
    default:
      return value || "-";
  }
}

function aiStatusSeverity(value) {
  switch (value) {
    case "completed":
      return "info";
    case "failed":
      return "warning";
    default:
      return "";
  }
}

function formatAiMatch(matches) {
  return matches ? localize(AI_TEXT.matchYes, "Matches") : localize(AI_TEXT.matchNo, "Does Not Match");
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(currentLocale.value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function toAbsoluteUrl(url) {
  if (!url) {
    return "";
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  return `${API_BASE}${url}`;
}

function phaseWidth(phase) {
  const duration = Number(job.value?.result?.durationSeconds || 0);
  if (!duration) {
    return 0;
  }

  return ((phase.endSeconds - phase.startSeconds) / duration) * 100;
}

function formatPhaseWeightText(phase) {
  return interpolate(localize(LOCALIZED_TEXT.phaseWeight), {
    phase: formatPhase(phase),
    value: `${phaseWidth(phase).toFixed(1)}%`,
  });
}

function formatPhaseMeaningText() {
  return localize(LOCALIZED_TEXT.phaseMeaning);
}

async function loadJob() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/analysis/jobs/${encodeURIComponent(route.params.jobId)}`, {
      timeout: 10000,
    });
    job.value = ensureApiObject(response, "Analysis job API");
  } catch (error) {
    console.error(error);
    job.value = null;
    errorMessage.value = error?.response?.status === 404 ? t("jobNotFound") : error?.message || t("loadResultFailed");
  } finally {
    loading.value = false;
  }
}

async function deleteJob() {
  const jobId = route.params.jobId;
  if (!jobId) {
    return;
  }

  if (!window.confirm(t("deleteJobConfirm"))) {
    return;
  }

  deleting.value = true;
  errorMessage.value = "";

  try {
    await axios.delete(`${API_BASE}/api/analysis/jobs/${encodeURIComponent(jobId)}`, {
      timeout: 10000,
    });
    router.push("/KendouPage/analyzer");
  } catch (error) {
    console.error(error);
    errorMessage.value = error?.response?.data?.message || error?.message || t("deleteJobFailed");
  } finally {
    deleting.value = false;
  }
}

function goBack() {
  router.push("/KendouPage/analyzer");
}

onMounted(loadJob);
</script>

<style scoped>
.analysis-detail-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.7), transparent 28%),
    radial-gradient(circle at 86% 82%, rgba(235, 190, 116, 0.28), transparent 26%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 24%, #f5eedf 48%, #eadab8 100%);
}

.analysis-shell {
  max-width: 1380px;
  margin: 0 auto;
  display: grid;
  gap: 18px;
}

.topbar,
.section-head,
.chip-row,
.phase-topline {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.panel-card,
.hero-card,
.state-card,
.error-panel,
.phase-card,
.metric-card,
.feedback-card {
  border: 1px solid var(--line);
  border-radius: 28px;
  background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%);
  box-shadow:
    0 22px 44px rgba(118, 87, 49, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.hero-card,
.panel-card {
  padding: 24px;
}

.hero-card {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 20px;
}

.eyebrow,
.section-tag,
.meta-box span {
  margin: 0;
  font-size: 0.82rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9c341d;
}

.hero-card h1,
.section-head h2 {
  margin: 0;
  color: var(--ink);
}

.lead,
.metric-card p,
.feedback-card p,
.empty-state {
  color: var(--ink-soft);
  line-height: 1.8;
}

.nav-chip {
  border-radius: 999px;
  border: 1px solid rgba(177, 144, 108, 0.7);
  background: rgba(255, 249, 240, 0.88);
  color: #5b4636;
  padding: 10px 15px;
  cursor: pointer;
}

.nav-chip.subtle {
  background: rgba(245, 236, 223, 0.95);
}

.danger-chip {
  background: rgba(171, 70, 33, 0.12);
  border-color: rgba(151, 51, 28, 0.45);
  color: #8a2613;
}

.meta-grid,
.metric-grid,
.feedback-list,
.phase-list,
.artifact-list,
.keyframe-grid,
.ai-grid,
.observation-list,
.split-stage {
  display: grid;
  gap: 14px;
}

.meta-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.split-stage {
  grid-template-columns: 1fr 1fr;
}

.split-stage.single {
  grid-template-columns: 1fr;
}

.meta-box,
.phase-card,
.metric-card,
.feedback-card,
.ai-card {
  padding: 16px 18px;
}

.meta-box strong,
.metric-value {
  color: var(--ink);
  font-size: 1.3rem;
}

.player {
  width: 100%;
  border-radius: 20px;
  background: #1a120a;
}

.artifact-list {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.artifact-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  border-radius: 18px;
  border: 1px solid rgba(177, 144, 108, 0.7);
  background: rgba(255, 249, 240, 0.88);
  color: #5b4636;
  text-decoration: none;
}

.keyframe-grid {
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}

.ai-grid {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  margin-top: 14px;
}

.ai-card {
  border: 1px solid rgba(177, 144, 108, 0.35);
  border-radius: 22px;
  background: rgba(255, 251, 245, 0.94);
  display: grid;
  gap: 12px;
}

.list-block {
  display: grid;
  gap: 8px;
}

.list-block p,
.list-block strong {
  margin: 0;
}

.plain-list {
  margin: 0;
  padding-left: 18px;
  color: var(--ink-soft);
  line-height: 1.7;
}

.keyframe-card {
  border: 1px solid rgba(177, 144, 108, 0.4);
  border-radius: 22px;
  overflow: hidden;
  background: rgba(255, 251, 245, 0.94);
}

.keyframe-image {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  object-position: center center;
  background: #1a120a;
}

.keyframe-body {
  padding: 16px 18px 18px;
  display: grid;
  gap: 12px;
}

.keyframe-meta {
  margin: 0;
  color: var(--ink-soft);
}

.observation-item {
  border: 1px solid rgba(177, 144, 108, 0.3);
  border-radius: 16px;
  padding: 12px 14px;
  background: rgba(255, 249, 240, 0.7);
}

.observation-item strong,
.observation-item span {
  color: var(--ink);
}

.observation-item p {
  margin: 6px 0 0;
}

.phase-note,
.metric-target-reason {
  margin: 8px 0 0;
  color: var(--ink-soft);
  line-height: 1.7;
}

.phase-note.subtle {
  margin-top: 4px;
  font-size: 0.94rem;
}

.phase-bar {
  height: 10px;
  background: rgba(221, 203, 180, 0.8);
  border-radius: 999px;
  overflow: hidden;
}

.phase-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(135deg, #b64926 0%, #8a2613 100%);
}

.soft-badge,
.status-chip,
.severity-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.soft-badge {
  background: rgba(235, 220, 201, 0.9);
  color: #6d5138;
}

.status-chip {
  background: linear-gradient(135deg, #31593f 0%, #16341f 100%);
  color: #fff7f0;
}

.severity-chip {
  background: rgba(235, 220, 201, 0.9);
  color: #6d5138;
}

.severity-chip.warning {
  background: linear-gradient(135deg, #b64926 0%, #8a2613 100%);
  color: #fff7f0;
}

.severity-chip.info {
  background: linear-gradient(135deg, #72583e 0%, #4f3b27 100%);
  color: #fff7f0;
}

@media (max-width: 980px) {
  .hero-card,
  .split-stage,
  .meta-grid {
    grid-template-columns: 1fr;
  }
}
</style>
